import React, { useEffect, useState } from 'react';
import { XMarkIcon, RectangleStackIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import {
  useAtualizarMetaCompraDimensao,
  useCriarMetaCompraDimensao,
  useExcluirMetaCompraDimensao,
} from '../../hooks/useMetaCompraDimensao';
import { useEmpresas } from '../../hooks/usePesquisas';
import { useToast } from '../ui/Toast';
import { useConfirmDialog } from '../ui/ConfirmDialog';
import InputMoeda from '../ui/InputMoeda';
import { inputIris, btnPrimario, btnSecundario, btnDanger } from '../ui/iris';
import AutocompleteDimensao from './AutocompleteDimensao';
import SugestaoMetaInteligente from '../metasCompra/SugestaoMetaInteligente';
import type {
  DimensaoOpcao,
  DimensaoTipo,
  MetaCompraDimensao,
} from '../../types/metaCompraDimensao';

interface Props {
  meta?: MetaCompraDimensao | null;
  defaultAnoMes?: string;
  defaultEmpresas?: string[];
  defaultDimensao?: DimensaoTipo;
  onClose: () => void;
}

function anoMesAtual(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const DIMENSOES: { value: DimensaoTipo; label: string }[] = [
  { value: 'fornecedor', label: 'Fornecedor' },
  { value: 'marca', label: 'Marca' },
  { value: 'grupo', label: 'Grupo' },
];

const ModalMetaCompraDimensao: React.FC<Props> = ({ meta, defaultAnoMes, defaultEmpresas, defaultDimensao, onClose }) => {
  const ehEdicao = !!meta;
  const toast = useToast();
  const confirm = useConfirmDialog();
  const { data: empresas = [] } = useEmpresas();
  const criarMutation = useCriarMetaCompraDimensao();
  const atualizarMutation = useAtualizarMetaCompraDimensao();
  const excluirMutation = useExcluirMetaCompraDimensao();

  const [dimensao, setDimensao] = useState<DimensaoTipo>(meta?.dimensao ?? defaultDimensao ?? 'fornecedor');
  const [dimensaoOpcao, setDimensaoOpcao] = useState<DimensaoOpcao | null>(
    meta ? { codigo: meta.codigoDimensao, nome: meta.nomeDimensao } : null,
  );
  const [empresasSel, setEmpresasSel] = useState<string[]>(meta?.empresas ?? defaultEmpresas ?? []);
  const [anoMes, setAnoMes] = useState<string>(meta?.anoMes ?? defaultAnoMes ?? anoMesAtual());
  const [descricao, setDescricao] = useState<string>(meta?.descricao ?? '');
  const [valorMeta, setValorMeta] = useState<number | null>(meta?.valorMeta ?? null);
  const [observacao, setObservacao] = useState<string>(meta?.observacao ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const toggleEmpresa = (cod: string) => {
    setEmpresasSel((prev) => prev.includes(cod) ? prev.filter((e) => e !== cod) : [...prev, cod]);
  };

  const trocarDimensao = (nova: DimensaoTipo) => {
    if (ehEdicao) return;
    setDimensao(nova);
    setDimensaoOpcao(null);  // limpa seleção ao trocar tipo
  };

  const validar = (): boolean => {
    const errs: Record<string, string> = {};
    if (!descricao.trim()) errs.descricao = 'Descrição obrigatória.';
    if (!ehEdicao) {
      if (!dimensaoOpcao) errs.dimensaoOpcao = `Selecione um ${dimensao}.`;
      if (empresasSel.length === 0) errs.empresas = 'Selecione ao menos 1 empresa.';
      if (!anoMes || !/^\d{4}-\d{2}$/.test(anoMes)) errs.anoMes = 'Mês obrigatório.';
    }
    if (valorMeta == null || valorMeta <= 0) errs.valorMeta = 'Valor deve ser maior que zero.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const salvar = async () => {
    if (!validar()) return;
    try {
      if (ehEdicao && meta) {
        await atualizarMutation.mutateAsync({
          id: meta.id,
          req: { descricao: descricao.trim(), valorMeta: valorMeta!, observacao: observacao || null },
        });
        toast.sucesso('Meta atualizada.');
      } else {
        await criarMutation.mutateAsync({
          anoMes,
          dimensao,
          codigoDimensao: dimensaoOpcao!.codigo,
          nomeDimensao: dimensaoOpcao!.nome,
          descricao: descricao.trim(),
          empresas: empresasSel,
          valorMeta: valorMeta!,
          observacao: observacao || null,
        });
        toast.sucesso('Meta cadastrada.');
      }
      onClose();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string }; status?: number } };
      const msg = e.response?.data?.message
        || (e.response?.status === 409 ? 'Empresa já está em outra meta neste mês.' : 'Falha ao salvar meta.');
      toast.erro(msg);
    }
  };

  const excluir = async () => {
    if (!meta) return;
    const ok = await confirm({
      titulo: 'Excluir meta',
      mensagem: `Remover meta "${meta.descricao}" (${meta.dimensao} ${meta.nomeDimensao})?`,
      destrutivo: true,
    });
    if (!ok) return;
    try {
      await excluirMutation.mutateAsync(meta.id);
      toast.sucesso('Meta excluída.');
      onClose();
    } catch {
      toast.erro('Falha ao excluir meta.');
    }
  };

  const isLoading = criarMutation.isPending || atualizarMutation.isPending || excluirMutation.isPending;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--scrim)] backdrop-blur-[2px] p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-2xl rounded-iris-lg border border-line-strong bg-surface shadow-iris-xl">
        <div className="flex items-start gap-2 border-b border-line px-4 py-3">
          <RectangleStackIcon className="mt-0.5 h-5 w-5 shrink-0 text-accent-text" aria-hidden />
          <h2 className="flex-1 text-sm font-semibold text-tx">
            {ehEdicao ? 'Editar meta por dimensão' : 'Cadastrar meta por dimensão'}
          </h2>
          <button type="button" onClick={onClose} className="rounded p-1 text-tx-2 hover:bg-surface-2" aria-label="Fechar">
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-3 p-4">
          {/* Dimensão (toggle) */}
          <div>
            <label className="mb-1 block text-[11px] uppercase tracking-wide text-tx-3">Tipo de dimensão</label>
            <div className="flex gap-1">
              {DIMENSOES.map((d) => (
                <button
                  key={d.value}
                  type="button"
                  onClick={() => trocarDimensao(d.value)}
                  disabled={ehEdicao}
                  className={`flex-1 rounded-iris-sm border px-2 py-1.5 text-xs font-medium transition-colors ${
                    dimensao === d.value
                      ? 'border-accent bg-accent-soft text-accent-text'
                      : 'border-line bg-surface text-tx-2 hover:bg-surface-2'
                  } ${ehEdicao ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>

          {/* Código + nome (autocomplete) */}
          <div>
            <label className="mb-1 block text-[11px] uppercase tracking-wide text-tx-3">
              {dimensao === 'fornecedor' ? 'Fornecedor' : dimensao === 'marca' ? 'Marca' : 'Grupo'}
            </label>
            <AutocompleteDimensao
              dimensao={dimensao}
              value={dimensaoOpcao}
              onChange={setDimensaoOpcao}
              disabled={ehEdicao}
              hasError={!!errors.dimensaoOpcao}
            />
            {errors.dimensaoOpcao && <p className="mt-1 text-xs text-danger">{errors.dimensaoOpcao}</p>}
            {ehEdicao && (
              <p className="mt-1 flex items-center gap-1 text-[10px] text-tx-3">
                <ExclamationTriangleIcon className="h-3 w-3" />
                Dimensão/código não podem ser alterados. Exclua e cadastre nova.
              </p>
            )}
          </div>

          {/* Descrição */}
          <div>
            <label className="mb-1 block text-[11px] uppercase tracking-wide text-tx-3">Descrição</label>
            <input
              type="text"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              placeholder='ex: "Meta Cravo Mai", "Top 10 Bebidas Q2"'
              maxLength={120}
              className={`w-full ${inputIris} ${errors.descricao ? 'border-danger' : ''}`}
            />
            {errors.descricao && <p className="mt-1 text-xs text-danger">{errors.descricao}</p>}
          </div>

          {/* Mês */}
          <div>
            <label className="mb-1 block text-[11px] uppercase tracking-wide text-tx-3">Mês / ano</label>
            <input
              type="month"
              value={anoMes}
              onChange={(e) => setAnoMes(e.target.value)}
              disabled={ehEdicao}
              className={`w-full ${inputIris} ${errors.anoMes ? 'border-danger' : ''}`}
            />
            {errors.anoMes && <p className="mt-1 text-xs text-danger">{errors.anoMes}</p>}
          </div>

          {/* Empresas */}
          <div>
            <label className="mb-1 block text-[11px] uppercase tracking-wide text-tx-3">
              Empresas ({empresasSel.length})
            </label>
            {ehEdicao ? (
              <div className="flex flex-wrap gap-1 rounded-iris-sm border border-line bg-surface-2 p-2 opacity-70">
                {meta!.empresas.map((cod) => (
                  <span key={cod} className="rounded-iris-sm border border-accent bg-accent-soft px-2 py-1 text-[11px] text-accent-text">
                    <span className="font-mono">{cod}</span>
                    <span className="ml-1">{empresas.find((e) => e.codigo === cod)?.nomeFantasia || ''}</span>
                  </span>
                ))}
              </div>
            ) : (
              <div className={`flex flex-wrap gap-1 rounded-iris-sm border bg-surface p-2 ${
                errors.empresas ? 'border-danger' : 'border-line'
              }`}>
                {empresas.map((e) => {
                  const ativa = empresasSel.includes(e.codigo);
                  return (
                    <button
                      key={e.codigo}
                      type="button"
                      onClick={() => toggleEmpresa(e.codigo)}
                      className={`rounded-iris-sm border px-2 py-1 text-[11px] transition-colors ${
                        ativa
                          ? 'border-accent bg-accent-soft text-accent-text'
                          : 'border-line bg-surface text-tx-2 hover:bg-surface-2'
                      }`}
                      title={e.razaosocial}
                    >
                      <span className="font-mono">{e.codigo}</span>
                      <span className="ml-1">{e.nomeFantasia || e.razaosocial}</span>
                    </button>
                  );
                })}
              </div>
            )}
            {errors.empresas && <p className="mt-1 text-xs text-danger">{errors.empresas}</p>}
          </div>

          {/* Sugestão inteligente baseada no último trimestre fechado.
              Renderiza quando empresas + código da dimensão estão preenchidos. */}
          <SugestaoMetaInteligente
            empresas={ehEdicao ? meta!.empresas : empresasSel}
            dimensao={dimensao}
            codigoDimensao={dimensaoOpcao?.codigo}
            onAplicar={setValorMeta}
          />

          {/* Valor */}
          <div>
            <label className="mb-1 block text-[11px] uppercase tracking-wide text-tx-3">Valor da meta</label>
            <InputMoeda
              value={valorMeta}
              onChange={setValorMeta}
              hasError={!!errors.valorMeta}
              ariaLabel="Valor da meta em reais"
            />
            {errors.valorMeta && <p className="mt-1 text-xs text-danger">{errors.valorMeta}</p>}
          </div>

          {/* Observação */}
          <div>
            <label className="mb-1 block text-[11px] uppercase tracking-wide text-tx-3">Observação (opcional)</label>
            <textarea
              value={observacao}
              onChange={(e) => setObservacao(e.target.value)}
              rows={2}
              maxLength={500}
              className="w-full resize-none rounded-iris-sm border border-line-strong bg-surface-2 px-3 py-1.5 text-sm text-tx placeholder:text-tx-3 focus:outline-none focus:bg-surface focus:border-accent focus:ring-[3px] focus:ring-accent-soft"
            />
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-line px-4 py-3">
          {ehEdicao ? (
            <button
              type="button"
              onClick={excluir}
              disabled={isLoading}
              className={btnDanger}
            >
              Excluir
            </button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} disabled={isLoading} className={btnSecundario}>
              Cancelar
            </button>
            <button type="button" onClick={salvar} disabled={isLoading} className={btnPrimario}>
              {isLoading ? 'Salvando…' : ehEdicao ? 'Salvar' : 'Cadastrar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalMetaCompraDimensao;
